type ObserverValue<O> = O extends Observer<infer V> ? V : never

type Subscriber = () => void

class Observer<V> {
	get value() {
		return this.#value
	}

	set value(value: V) {
		this.#value = value
		this.#notify()
	}

	#subscribers = new Set<Subscriber>()

	#value: V

	constructor(initial: V) {
		this.#value = initial
	}

	subscribe = (subscriber: Subscriber, signal?: AbortSignal) => {
		this.#subscribers.add(subscriber)
		const unsubscribe = () => this.#subscribers.delete(subscriber)

		signal?.addEventListener('abort', unsubscribe, { once: true })

		return unsubscribe
	}

	#notify() {
		this.#subscribers.forEach(subscriber => subscriber())
	}
}

class Computed<
	V,
	T extends Observer<any>[] = Observer<any>[]
> extends Observer<V> {
	#compute: () => V
	constructor(
		...props: [...T, (...values: { [K in keyof T]: ObserverValue<T[K]> }) => V]
	) {
		const getValue = props.pop() as (...values: T) => V
		const observers = props as any as T

		const compute = () => getValue(...(observers.map(o => o.value) as T))

		super(compute())

		this.#compute = compute

		observers.forEach(observer => observer.subscribe(this.#recalculate))
	}

	#recalculate = () => {
		this.value = this.#compute()
	}
}

export { Computed, Observer }
